// main imports
import React, { Component } from "react";
import { History, Location } from "history";
import { match } from "react-router-dom";

import { weekNumber, stringCompare } from "@/lib/util";
import { PEOPLE_TAGS, PEOPLE_TAGS_ACCESS, PEOPLE_TAGS_DEFAULT } from "@/lib/ManufacturingTags.generated";

// backend
import { getBackend } from "@/lib/backend/backend2";
import * as t from "@/lib/backend/manufacturing2.generated.types";
import { translate } from "@/modules/shared/IntlProviderWrapper";
import { lineGroupUsesAdvancedPoolScheduling } from "./shiftBoard";

// import subcomponents
import ErrorComponent from "@/modules/shared/ui/ErrorComponent";
import ShiftAssignmentsPeople from "./ShiftAssignmentsPeople";
import ShiftAssignmentDetails from "../details/ShiftAssignmentDetails";

import type { PersonStats } from "@/modules/dt/masterData/people/PeopleList";
import type { IPeopleScheduleTemplate } from "@/lib/backend/manufacturing2.generated.types";

// navigation filter
export type ShiftsFilterProps = {
    year: number;
    week: number;
};

export function initShiftFilterProps(): ShiftsFilterProps {
    // set current year and week
    let now = new Date();
    let week = weekNumber(now);
    let year = now.getFullYear();
    // if we have week 1 extending into previous year
    if (now.getMonth() === 11 && week === 1) {
        year = year + 1;
    }
    // return defaults
    return { week, year };
}

type Props = {
    match: match<{ view_params?: string }>;
    history: History;
    location: Location;
    year: number;
    week: number;
    selected_plant: string;
    selected_line_group: string;
    template_mode: boolean;
    template: IPeopleScheduleTemplate | null;
    onTemplateChange: (template: IPeopleScheduleTemplate | null) => void;
    onWeekChange: (increment: number) => void;
    onShiftSaveStateChange: (inProgress: boolean) => void;
};

type State = {
    people: t.IPersonData[];
    people_stats?: Map<string, PersonStats>;
    competences: Map<string, number>;
    error: string;
    selected: t.IPersonData | null;
    name_filter: string;
    isLoading: boolean;
};

/**
 * Displaying people's shift-assignments list.
 */
class ShiftAssignments extends Component<Props, State> {
    shiftAssignmentDetailsRef = React.createRef<ShiftAssignmentDetails>();
    unique_person_names = new Map<string, string>();
    people_by_uuid = new Map<string, t.IPersonData>();

    state: State = {
        people: [],
        competences: new Map(),
        error: "",
        selected: null,
        name_filter: "",
        isLoading: false,
    };

    /**
     * Actual loading of data from backend.
     */
    async loadComponent() {
        const isAdvancedPoolSchedulingActive = lineGroupUsesAdvancedPoolScheduling(
            this.props.selected_plant,
            this.props.selected_line_group
        );

        this.setState({ isLoading: true });
        try {
            // get available people
            const { people } = await getBackend().manufacturing.getPeople({
                include_competences: isAdvancedPoolSchedulingActive,
            });
            people.sort((a, b) => stringCompare(a.name, b.name));

            // build lookup map of people by UUID
            this.people_by_uuid = new Map(people.map(person => [person.uuid, person]));

            // prepare cache of competences
            const res_competences = await getBackend().manufacturing.getLineCompetence({
                linegroup_uuid: this.props.selected_line_group,
            });
            const competences: Map<string, number> = new Map();
            res_competences.competences.forEach(x => {
                competences.set(`${x.person_uuid}_${x.line_uuid}`, x.level);
            });

            // make the first active person as selected
            const people_active = people.filter(person => person.active);
            if (people_active.length === 0) {
                return;
            }
            let selected: t.IPersonData | null = null;

            // if a person is selected in URI, use that one
            const selected_uuid = this.props.match.params.view_params;
            if (selected_uuid) {
                selected = this.people_by_uuid.get(selected_uuid) || null;
            }

            let people_stats: Map<string, PersonStats> | undefined;
            if (!isAdvancedPoolSchedulingActive) {
                people_stats = this.initPeopleStats(people);
            }

            this.setState({
                people,
                people_stats,
                competences,
                selected,
                error: "",
            });
        } catch (error) {
            this.setState({ error: translate("common.error", "An error occurred while fetching data") });
        } finally {
            this.setState({ isLoading: false });
        }
    }

    initPeopleStats(people: t.IPersonData[]): Map<string, PersonStats> {
        const people_stats: Map<string, PersonStats> = new Map();
        // Calculate max hours for each person based on person's:
        // - max hours per shift
        // - max shifts per week
        for (const person of people) {
            const person_stats = {
                hours_max: 0,
                hours_assigned: 0,
            };
            const max_shifts = Number(PEOPLE_TAGS_ACCESS.reduced_worktime(person.tags));
            const work_time = PEOPLE_TAGS_ACCESS.max_h_per_shift(person.tags);
            person_stats.hours_max = max_shifts * work_time;
            people_stats.set(person.uuid, person_stats);
        }

        return people_stats;
    }

    /**
     * Creating a unique name for a person.
     */
    createUniquePersonName(selectedPerson: t.IPersonData): string {
        // find selected abbrevation - unique name
        const names = selectedPerson.name.split(" ");
        let not_unique_name = true;
        let i = 0;
        let sname = "";
        while (not_unique_name) {
            let unique_name = true;
            sname = names[0];
            if (i > 0 && names.length > 1) {
                sname += " " + names[1].slice(0, i) + ".";
            }
            this.state.people.forEach(person => {
                if (selectedPerson.uuid !== person.uuid) {
                    const tnames = person.name.split(" ");
                    let tname = tnames[0];
                    if (i > 0 && tnames.length > 1) {
                        tname += " " + tnames[1].slice(0, i) + ".";
                    }
                    if (tname === sname) {
                        unique_name = false;
                    }
                }
            });

            not_unique_name = !unique_name;
            // give up after few iterations
            if (i > 10) {
                not_unique_name = false;
            }
            i++;
        }

        return sname;
    }

    uniquePersonName(uuid: string): string {
        if (uuid === "") return "";
        if (uuid === null) return "";
        if (uuid === undefined) return "";

        const name = this.unique_person_names.get(uuid);
        if (name !== undefined) {
            return name;
        }

        const person = this.people_by_uuid.get(uuid);
        if (person === undefined) {
            return "";
        }

        const unique_name = this.createUniquePersonName(person);
        this.unique_person_names.set(uuid, unique_name);
        return unique_name;
    }

    isPersonAdjuster(uuid: string): boolean {
        if (uuid === "") return false;
        if (uuid === null) return false;
        if (uuid === undefined) return false;

        const person = this.people_by_uuid.get(uuid);
        if (person === undefined) {
            return false;
        }

        return person.adjuster;
    }

    /**
     * Handling click on a particular person. Remembering the selected person.
     */
    handlePeopleClick(person: t.IPersonData) {
        this.setState({ selected: person });

        // update url with each click
        this.props.history.push("/manufacturing/people/shifts/" + person.uuid);
    }

    handlePersonClick = (person_uuid: string) => {
        const person = this.people_by_uuid.get(person_uuid);
        if (person === undefined) {
            return;
        }

        this.handlePeopleClick(person);
    };

    componentDidMount() {
        this.loadComponent();
    }

    componentDidUpdate(prevProps: Props) {
        if (
            prevProps.selected_line_group !== this.props.selected_line_group ||
            prevProps.selected_plant !== this.props.selected_plant
        ) {
            this.setState({ selected: null });
            this.loadComponent();
        }
    }

    handleScheduleUpdate = (lines: t.IShiftPersonLineRec[]) => {
        if (lineGroupUsesAdvancedPoolScheduling(this.props.selected_plant, this.props.selected_line_group)) {
            return;
        }

        const current_people_stats: Map<string, PersonStats> = this.state.people_stats ?? new Map();
        const new_people_stats: Map<string, PersonStats> = new Map();

        // Reset number of assigned hours (needs to be recalculated)
        for (const [uuid, stats] of current_people_stats.entries()) {
            new_people_stats.set(uuid, { ...stats, hours_assigned: 0 });
        }

        // Recalculate number of assigned hours from updated schedule
        const person_hours = new Map<string, number>();
        for (let line of lines) {
            for (let shift of line.shifts) {
                for (let person of shift.persons) {
                    const stats = new_people_stats.get(person.uuid);
                    if (stats !== undefined) {
                        let hours = person_hours.get(person.uuid) ?? 0;
                        // Divide work hours evenly between all workers on the same shift
                        hours += shift.worker_hours / shift.persons.length;
                        person_hours.set(person.uuid, hours);
                        stats.hours_assigned = hours;
                    }
                }
            }
        }

        this.setState({ people_stats: new_people_stats });
    };

    handleShiftsSaved() {}

    /**
     * Handling error from subordinate component.
     */
    handleToError = (str: string) => {
        this.setState({ error: str });
    };

    /**
     * Forward handlers
     */
    handleCopyPreviousWeek = () => {
        this.shiftAssignmentDetailsRef.current?.copyPreviousWeek();
    };

    exportToHtmlWeekend = () => {
        this.shiftAssignmentDetailsRef.current?.exportToHtmlWeekend();
    };

    handleShiftPrintWeek = (params: {
        include_prev_sunday?: boolean;
        include_next_sunday?: boolean;
        columns_as_shifts?: boolean;
    }) => {
        this.shiftAssignmentDetailsRef.current?.exportToHtmlWeek(params);
    };

    handleShiftPrintDay = (day_of_week: string) => {
        this.shiftAssignmentDetailsRef.current?.startExportToHtmlDay(day_of_week);
    };

    handleSaveChanges = () => {
        this.shiftAssignmentDetailsRef.current?.saveChanges();
    };

    handleSaveChangesSync = async () => {
        await this.shiftAssignmentDetailsRef.current?.saveChanges();
    };

    handleApplyShiftTemplate = (): boolean => {
        return this.shiftAssignmentDetailsRef.current?.applyShiftTemplate() ?? false;
    };

    /**
     * Handling production line submit event.
     */
    handleSubmit = (e: Event) => {
        e.preventDefault();
    };

    canPersonWorkInShift(person_uuid: string, shift_of_day: number): boolean {
        const person = this.people_by_uuid.get(person_uuid);
        if (person === undefined) {
            return false;
        }

        return person.shift_preference[shift_of_day] || false;
    }

    canPersonWorkOnLine(person_uuid: string, line_uuid: string): boolean {
        const person = this.people_by_uuid.get(person_uuid);
        if (person === undefined) {
            return false;
        }

        return this.state.competences.has(`${person_uuid}_${line_uuid}`);
    }

    getPersonCompetenceLevel(person_uuid: string, line_uuid: string): number {
        return this.state.competences.get(`${person_uuid}_${line_uuid}`) || 0;
    }

    personMaxNumberShifts(person_uuid: string) {
        // prepare default value
        const default_shifts: number = parseInt(PEOPLE_TAGS_DEFAULT.reduced_worktime, 10);
        // check if we have the person
        const person = this.people_by_uuid.get(person_uuid);
        if (person === undefined) {
            return default_shifts;
        }

        // check if we have a tag
        if (person.tags[PEOPLE_TAGS.reduced_worktime] !== undefined) {
            return parseInt(person.tags[PEOPLE_TAGS.reduced_worktime], 10);
        }

        return default_shifts;
    }

    personWorks12Hours(person_uuid: string): boolean {
        const person = this.people_by_uuid.get(person_uuid);
        if (person === undefined) {
            return false;
        }

        if (person.tags[PEOPLE_TAGS.shift_board_works_12h_shifts] === "true") {
            return true;
        }
        return false;
    }

    personAllowedShifts(person_uuid: string): boolean[] {
        // prepare default value
        const default_shifts: boolean[] = [true, true, true];
        // check if we have the person
        const person = this.people_by_uuid.get(person_uuid);
        if (person === undefined) {
            return default_shifts;
        }

        return person.shift_preference;
    }

    render() {
        return (
            <article className="article h-100">
                <section className="shift-assignments">
                    <div id="shift-assignments-section">
                        <ShiftAssignmentsPeople
                            history={this.props.history}
                            handlePeopleClick={person => {
                                this.handlePeopleClick(person);
                            }}
                            people={this.state.people}
                            people_stats={this.state.people_stats}
                            selected={this.state.selected}
                            selectedPlant={this.props.selected_plant}
                            selectedLineGroup={this.props.selected_line_group}
                        />
                        {this.state.error !== "" ? (
                            <ErrorComponent className="m-3" msg={this.state.error} type="error" />
                        ) : (
                            <ShiftAssignmentDetails
                                uniquePersonName={str => this.uniquePersonName(str)}
                                isPersonAdjuster={str => this.isPersonAdjuster(str)}
                                selectedPerson={this.state.selected}
                                week={this.props.week}
                                year={this.props.year}
                                plant={this.props.selected_plant}
                                line_group_uuid={this.props.selected_line_group}
                                ref={this.shiftAssignmentDetailsRef}
                                onScheduleUpdate={this.handleScheduleUpdate}
                                onPersonClick={this.handlePersonClick}
                                onShiftsSaved={() => this.handleShiftsSaved()}
                                onShiftSaveStateChange={this.props.onShiftSaveStateChange}
                                canPersonWorkOnLine={(person_uuid: string, line_uuid: string) =>
                                    this.canPersonWorkOnLine(person_uuid, line_uuid)
                                }
                                canPersonWorkInShift={(person_uuid: string, shift_of_day: number) =>
                                    this.canPersonWorkInShift(person_uuid, shift_of_day)
                                }
                                personMaxNumberShifts={(person_uuid: string) => this.personMaxNumberShifts(person_uuid)}
                                personAllowedShifts={(person_uuid: string) => this.personAllowedShifts(person_uuid)}
                                personWorks12Hours={(person_uuid: string) => this.personWorks12Hours(person_uuid)}
                                getPersonCompetenceLevel={(person_uuid: string, line_uuid: string) =>
                                    this.getPersonCompetenceLevel(person_uuid, line_uuid)
                                }
                                people={this.state.people}
                                template_mode={this.props.template_mode}
                                template={this.props.template}
                                onTemplateChange={this.props.onTemplateChange}
                                onWeekChange={this.props.onWeekChange}
                                isLoading={this.state.isLoading}
                            />
                        )}
                    </div>
                </section>
            </article>
        );
    }
}

export default ShiftAssignments;
